import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UserSelect from '../components/UserSelect';
import ActivityModal from '../components/Workorder/EditActivityModal';
import AdditionModal from '../components/Workorder/EditAdditionModal';
import BarrelToppingModal from '../components/Workorder/BarrelToppingModal';
import BarrelSelectionModal from '../components/Workorder/BarrelSelectionModal';
import axios from 'axios';
import '../index.css';
import { PlusCircle, Package } from 'lucide-react';

const WorkorderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wines, setWines] = useState([]);
  const [activities, setActivities] = useState([]);
  const [additions, setAdditions] = useState([]);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showAdditionModal, setShowAdditionModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingAddition, setEditingAddition] = useState(null);
  const [showBarrelModal, setShowBarrelModal] = useState(false);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState(null);
  const [wineContainers, setWineContainers] = useState({});
  const [showToppingModal, setShowToppingModal] = useState(false);
  const [newActivity, setNewActivity] = useState({
    type: '',
    description: '',
    startGallons: '',
    endGallons: '',
    difference: 0,
    selectedBarrels: []
  });

  const [workorder, setWorkorder] = useState({
    date: new Date().toISOString().split('T')[0],
    wineID: '',
    status: 'Open',
    instructions: '',
    assignedTo: '',
    completedDate: null
  });

  const activityTypes = [
    'Pressing',
    'Filtering',
    'Racking',
    'Tank Transfer',
    'Barrel Work',
    'Topping',
    'Sampling',
    'Cleaning',
    'Other'
  ];

  const measureTypes = ['Each', 'ml', 'L', 'Gal', 'grams', 'kg'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch active wines
        const winesResponse = await axios.get('/api/wines?active=1');
        setWines(winesResponse.data.wines);

        if (id && id !== 'new') {
          const workorderResponse = await axios.get(`/api/workorders/${id}`);
          const workorderData = workorderResponse.data;
          
          setWorkorder({
            ...workorderData,
            date: new Date(workorderData.date).toISOString().split('T')[0] // Format date for input
          });
          
          setActivities(workorderData.activities || []);
          setAdditions(workorderData.additions || []);
          
          // Fetch containers for the wine
          if (workorderData.wineID) {
            await fetchContainersForWine(workorderData.wineID);
          }

          // If topping activities exist, fetch source wine containers too
          for (const activity of workorderData.activities || []) {
            if (activity.type === 'Topping' && activity.sourceWineID && activity.sourceWineID !== workorderData.wineID) {
              await fetchContainersForWine(activity.sourceWineID);
            }
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const fetchContainersForWine = async (wineID) => {
    try {
      // Only fetch if we don't already have this wine's containers
      if (!wineContainers[wineID]) {
        const response = await axios.get(`/api/wines/${wineID}/containers`);
        setWineContainers(prev => ({
          ...prev,
          [wineID]: response.data
        }));
      }
      return wineContainers[wineID] || [];
    } catch (err) {
      console.error('Error fetching containers:', err);
      setError('Failed to load barrels for this wine');
      return [];
    }
  };

  const handleWineChange = async (e) => {
    const wineID = e.target.value;
    setWorkorder({ ...workorder, wineID });
    
    if (wineID) {
      await fetchContainersForWine(wineID);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const workorderData = {
        ...workorder,
        activities,
        additions
      };

      if (id && id !== 'new') {
        await axios.put(`/api/workorders/${id}`, workorderData);
      } else {
        await axios.post('/api/workorders', workorderData);
      }

      navigate('/workorders');
    } catch (error) {
      console.error('Error saving workorder:', error);
      setError('Failed to save workorder');
    }
  };

  const handleAddActivity = (activity) => {
    setActivities([...activities, { ...activity, id: Date.now() }]);
    setShowActivityModal(false);
  };

  const handleAddAddition = (addition) => {
    setAdditions([...additions, { ...addition, id: Date.now() }]);
    setShowAdditionModal(false);
  };

  const handleComplete = async () => {
    if (!workorder.assignedTo) {
      setError('Please assign the workorder before completing');
      return;
    }

    try {
      const updatedWorkorder = {
        ...workorder,
        status: 'Completed',
        completedDate: new Date().toISOString()
      };

      await axios.put(`/api/workorders/${id}`, {
        ...updatedWorkorder,
        activities,
        additions
      });
      
      navigate('/workorders');
    } catch (error) {
      console.error('Error completing workorder:', error);
      setError('Failed to complete workorder');
    }
  };

  const handleOpenBarrelSelection = (activityIndex) => {
    setSelectedActivityIndex(activityIndex);
    setShowBarrelModal(true);
  };

  const handleBarrelSelectionSave = (selectedBarrels) => {
    if (selectedActivityIndex !== null) {
      // Updating existing activity
      const updatedActivities = [...activities];
      updatedActivities[selectedActivityIndex] = {
        ...updatedActivities[selectedActivityIndex],
        selectedBarrels
      };
      setActivities(updatedActivities);
    } else {
      // For new activity
      setNewActivity({
        ...newActivity,
        selectedBarrels
      });
    }
    setShowBarrelModal(false);
    setSelectedActivityIndex(null);
  };

// Modify the handleAddTopping function to handle reassignments
  const handleAddTopping = async (toppingActivity) => {
    // If we have a new source wine in the topping activity, make sure to fetch its containers
    if (toppingActivity.sourceWineID && !wineContainers[toppingActivity.sourceWineID]) {
      await fetchContainersForWine(toppingActivity.sourceWineID);
    }
    
    // Create activities array with the topping activity
    const newActivities = [...activities, { ...toppingActivity, id: Date.now() }];
    
    // If there are reassignments, create an additional racking activity
    if (toppingActivity.reassignments && toppingActivity.reassignments.length > 0) {
      const totalReassigned = toppingActivity.reassignments
        .filter(r => r.selected)
        .reduce((sum, r) => sum + r.gallons, 0)
        .toFixed(2);
        
      const reassignmentDesc = toppingActivity.reassignments
        .filter(r => r.selected)
        .map(r => `${r.containerType} (${r.gallons} gal)`)
        .join(', ');
        
      // Add a racking activity for the reassignment
      newActivities.push({
        id: Date.now() + 1,
        type: 'Racking',
        description: `Container reassignment: ${totalReassigned} gal to: ${reassignmentDesc}`,
        sourceWineID: toppingActivity.sourceWineID,
        startGallons: null,
        endGallons: null,
        notes: `Reassignment of remaining wine from topping activity`
      });
    }
    
    setActivities(newActivities);
    setShowToppingModal(false);
  };

  const getBarrelCountLabel = (activity) => {
    const barrels = activity.selectedBarrels || [];
    
    if (activity.type === 'Topping') {
      // For topping activities
      const sourceWineID = activity.sourceWineID || workorder.wineID;
      const sourceWine = wines.find(w => w.wineID == sourceWineID);
      const sourceText = sourceWine ? `${sourceWine.varietal} - Container #${activity.sourceBarrel}` : `Container #${activity.sourceBarrel}`;
      const destinations = activity.destinationBarrels?.length || 0;
      
      return `Source: ${sourceText}, Destinations: ${destinations}`;
    }
    
    // For regular activities
    return barrels.length > 0 ? `${barrels.length} barrels selected` : 'No barrels selected';
  };

  const getActivityDescription = (activity) => {
    // For topping activities, enhance the description with wine details
    if (activity.type === 'Topping' && activity.sourceWineID) {
      const sourceWine = wines.find(w => w.wineID == activity.sourceWineID);
      if (sourceWine && sourceWine.wineID != workorder.wineID) {
        return `${activity.description} (from ${sourceWine.vintage} ${sourceWine.varietal})`;
      }
    }
    return activity.description;
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{id === 'new' ? 'New Workorder' : 'Edit Workorder'}</h2>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => navigate('/workorders')}
        >
          Back to List
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* Basic Info Card */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={workorder.date}
                  onChange={(e) => setWorkorder({ ...workorder, date: e.target.value })}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Wine Lot</label>
                <select
                  className="form-control"
                  value={workorder.wineID}
                  onChange={handleWineChange}
                  required
                >
                  <option value="">Select Wine Lot</option>
                  {wines.map((wine) => (
                    <option key={wine.wineID} value={wine.wineID}>
                      {wine.wineID} - {wine.vintage} {wine.varietal} - {wine.vineyard || 'Unknown'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Instructions</label>
              <textarea
                className="form-control"
                rows="3"
                value={workorder.instructions}
                onChange={(e) => setWorkorder({ ...workorder, instructions: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Activities Section */}
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Activities</h5>
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowActivityModal(true)}
              >
                <PlusCircle size={16} className="me-1" />
                Add Activity
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowToppingModal(true)}
                disabled={!workorder.wineID}
              >
                <Package size={16} className="me-1" />
                Barrel Topping
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
            <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Diff.</th>
                    <th>Barrels</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((activity, index) => (
                    <tr 
                      key={activity.id || index}
                      onClick={() => setEditingActivity(activity)}
                      style={{ cursor: 'pointer' }}
                      className="table-row-hover"
                    >
                      <td>{activity.type}</td>
                      <td>{getActivityDescription(activity)}</td>
                      <td>{activity.startGallons || '-'}</td>
                      <td>{activity.endGallons || '-'}</td>
                      <td className={
                        activity.difference
                          ? activity.difference < 0
                            ? 'text-danger'
                            : 'text-success'
                          : ''
                      }>
                        {activity.difference ? activity.difference.toFixed(2) : '-'}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenBarrelSelection(index);
                          }}
                        >
                          {getBarrelCountLabel(activity)}
                        </button>
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActivities(activities.filter((_, i) => i !== index));
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Additions Section */}
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Additions</h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowAdditionModal(true)}
            >
              <PlusCircle size={16} className="me-1" />
              Add Product
            </button>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Measure</th>
                    <th>Preparation Instructions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {additions.map((addition, index) => (
                    <tr 
                      key={addition.id || index}
                      onClick={() => setEditingAddition(addition)}
                      style={{ cursor: 'pointer' }}
                      className="table-row-hover"
                    >
                      <td>{addition.product}</td>
                      <td>{addition.quantity}</td>
                      <td>{addition.measure}</td>
                      <td>{addition.prepInstructions}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAdditions(additions.filter((_, i) => i !== index));
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Completion Section */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Assigned To</label>
                <UserSelect
                  value={workorder.assignedTo}
                  onChange={(e) => setWorkorder({ ...workorder, assignedTo: e.target.value })}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-control"
                  value={workorder.status}
                  onChange={(e) => setWorkorder({ ...workorder, status: e.target.value })}
                  disabled={workorder.status === 'Completed'}
                >
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => navigate('/workorders')}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-sm btn-outline-primary"
                >
                  Save
                </button>
                {workorder.status !== 'Completed' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={handleComplete}
                    disabled={!workorder.assignedTo}
                  >
                    Complete Workorder
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Activity Modal */}
      <ActivityModal
        show={showActivityModal}
        activity={newActivity}
        activityTypes={activityTypes}
        wineID={workorder.wineID}
        wineContainers={wineContainers}
        onClose={() => setShowActivityModal(false)}
        onSave={handleAddActivity}
        onBarrelSelection={() => handleOpenBarrelSelection(null)}
      />

      {/* Addition Modal */}
      <AdditionModal
        show={showAdditionModal}
        addition={null}
        measureTypes={measureTypes}
        onClose={() => setShowAdditionModal(false)}
        onSave={handleAddAddition}
      />

      {/* Enhanced Barrel Topping Modal with cross-lot topping functionality */}
      <BarrelToppingModal
        show={showToppingModal}
        wineID={workorder.wineID}
        wineContainers={wineContainers}
        onClose={() => setShowToppingModal(false)}
        onSave={handleAddTopping}
      />

      {/* Barrel Selection Modal */}
      <BarrelSelectionModal
        show={showBarrelModal}
        wineID={workorder.wineID}
        wineContainers={wineContainers}
        selectedBarrels={selectedActivityIndex !== null 
          ? activities[selectedActivityIndex]?.selectedBarrels || [] 
          : newActivity.selectedBarrels || []}
        onClose={() => {
          setShowBarrelModal(false);
          setSelectedActivityIndex(null);
        }}
        onSave={handleBarrelSelectionSave}
      />

      {/* Edit Activity Modal */}
      <ActivityModal
        show={editingActivity !== null}
        activity={editingActivity || {}}
        activityTypes={activityTypes}
        wineID={workorder.wineID}
        wineContainers={wineContainers}
        onClose={() => setEditingActivity(null)}
        onSave={(updatedActivity) => {
          if (!updatedActivity) return;
          setActivities(activities.map(activity => 
            activity.id === updatedActivity.id ? updatedActivity : activity
          ));
          setEditingActivity(null);
        }}
        onBarrelSelection={(activity) => {
          const index = activities.findIndex(a => a.id === activity.id);
          if (index !== -1) {
            handleOpenBarrelSelection(index);
          }
          setEditingActivity(null);
        }}
      />

      {/* Edit Addition Modal */}
      <AdditionModal
        show={editingAddition !== null}
        addition={editingAddition || {}}
        measureTypes={measureTypes}
        onClose={() => setEditingAddition(null)}
        onSave={(updatedAddition) => {
          if (!updatedAddition) return;
          setAdditions(additions.map(addition => 
            addition.id === updatedAddition.id ? updatedAddition : addition
          ));
          setEditingAddition(null);
        }}
      />

      <style jsx>{`
        .table-row-hover:hover td {
          background-color: rgba(0,0,0,.075);
        }
        .table-row-hover {
          transition: background-color 0.2s ease;
        }
        .btn svg {
          vertical-align: middle;
          margin-top: -2px;
        }
        .btn:hover svg {
          transform: scale(1.1);
          transition: transform 0.2s ease;
        }
        .form-check-input:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        .card {
          transition: all 0.2s ease;
        }
        .card:hover {
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        }
      `}</style>
    </div>
  );
};

export default WorkorderDetail;